import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormViewParams, SuspendedPopovers } from '../models/state.model';
import { ViewResponse } from '../../models/viewResponse/ViewResponse';
import { FormContextValue } from '../../contexts/FormContext';

export type PageViewStack = Array<FormViewParams>;

export type SetupViewStackMeta = {
  navigation?: NavigationType;
};

export type NavigationType = 'PUSH' | 'REPLACE';

const PageViewSlice = createSlice({
  name: 'pageViewStack',
  initialState: [] as PageViewStack,
  reducers: {
    setupViewStack: {
      reducer(_, action: PayloadAction<PageViewStack>) {
        sessionStorage.setItem('viewStack', JSON.stringify(action.payload));
        return action.payload;
      },
      prepare(payload: PageViewStack, meta?: SetupViewStackMeta) {
        return {
          payload,
          meta: {
            navigation: meta?.navigation ?? 'PUSH',
          },
        };
      },
    },
    setPageViewData(state, action) {
      if (state.length) {
        state[state.length - 1].dataRequest = action.payload;
      }
      sessionStorage.setItem('viewStack', JSON.stringify(state));
      return state;
    },
    openPageView(stack, action: PayloadAction<FormViewParams>) {
      stack.push(action.payload);
      sessionStorage.setItem('viewStack', JSON.stringify(stack));
    },
    closePageView(stack) {
      stack.pop();
      sessionStorage.setItem('viewStack', JSON.stringify(stack));
    },
    clearViewStack() {
      sessionStorage.removeItem('viewStack');
      return [];
    },
    suspendPopovers(state, action: PayloadAction<SuspendedPopovers>) {
      if (state.length) {
        state[state.length - 1].suspendedPopovers = action.payload;
      }
      sessionStorage.setItem('viewStack', JSON.stringify(state));
    },
    clearSuspendedPopovers(state) {
      if (state.length) {
        delete state[state.length - 1].suspendedPopovers;
      }
      sessionStorage.setItem('viewStack', JSON.stringify(state));
    },
    hydrateViewStack(_, action: PayloadAction<PageViewStack>) {
      sessionStorage.setItem('viewStack', JSON.stringify(action.payload));
      return action.payload;
    },
    updateViewBreadcrumbTitle(
      state,
      action: PayloadAction<{
        viewRequest: FormViewParams['viewRequest'];
        viewResponse: ViewResponse;
        title: string;
      }>,
    ) {
      const { viewRequest, title } = action.payload;

      const targetView = state.find(
        (v) =>
          v.viewRequest?.moduleKey === viewRequest?.moduleKey &&
          v.viewRequest?.type === viewRequest?.type &&
          v.viewRequest?.configName === viewRequest?.configName &&
          v.viewRequest?.id === viewRequest?.id,
      );

      if (!targetView?.breadcrumbs) return state;

      let changed = false;

      Object.keys(targetView.breadcrumbs).forEach((area) => {
        targetView.breadcrumbs[area] = targetView.breadcrumbs[area].map((crumb) => {
          if (crumb.title === title) return crumb;

          if (crumb.title) return crumb;

          changed = true;

          return {
            ...crumb,
            title,
          };
        });
      });

      if (!changed) return state;

      sessionStorage.setItem('viewStack', JSON.stringify(state));

      return state;
    },
    setPageContext(state, action: PayloadAction<FormContextValue>) {
      if (state.length) {
        const page = state[state.length - 1];

        if (!page.context) {
          page.context = [];
        }

        page.context.push(action.payload);
      }

      return state;
    },
  },
});

export const {
  setupViewStack,
  openPageView,
  closePageView,
  setPageViewData,
  clearViewStack,
  suspendPopovers,
  clearSuspendedPopovers,
  hydrateViewStack,
  updateViewBreadcrumbTitle,
  setPageContext,
} = PageViewSlice.actions;

const PageViewsReducer = PageViewSlice.reducer;
export default PageViewsReducer;
